import { ChangelogEntry, ChangelogIndex } from '../interfaces/Changelog';

const BAZA = '/changelog';

/**
 * Citește jurnalul de noutăți din `public/changelog/`.
 *
 * Totul e tolerant la lipsă: dacă fișierele nu există (deploy vechi, build fără changelog),
 * metodele întorc gol în loc să arunce, iar interfața se degradează grațios.
 */
export class ChangelogService {
  /** Cache pe durata sesiunii, ca să nu refacem fetch-ul la fiecare deschidere de modal. */
  private static cacheIndex: ChangelogEntry[] | null = null;
  private static cacheDocs: Record<string, string> = {};

  /**
   * Lista versiunilor, cea mai nouă prima.
   *
   * `no-store` + parametru de timp: popup-ul întreabă tocmai pentru că versiunea de pe server e
   * mai nouă decât cea din browser, deci un răspuns din cache ar rata exact intrarea căutată.
   */
  static async getIndex(): Promise<ChangelogEntry[]> {
    if (this.cacheIndex) return this.cacheIndex;

    try {
      const raspuns = await fetch(`${BAZA}/index.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!raspuns.ok) return [];

      const date = (await raspuns.json()) as ChangelogIndex;
      const versiuni = Array.isArray(date?.versions) ? date.versions : [];
      this.cacheIndex = versiuni;
      return versiuni;
    } catch (error) {
      console.warn('Nu am putut citi jurnalul de versiuni:', error);
      return [];
    }
  }

  /** Intrarea pentru o versiune anume, sau `null` dacă nu e documentată. */
  static async getEntry(version: string | null): Promise<ChangelogEntry | null> {
    if (!version) return null;
    const versiuni = await this.getIndex();
    return versiuni.find((v) => v.version === version) ?? null;
  }

  /** Documentația markdown a unei versiuni. Șir gol dacă lipsește. */
  static async getDoc(entry: ChangelogEntry): Promise<string> {
    if (this.cacheDocs[entry.version] !== undefined) return this.cacheDocs[entry.version];

    try {
      const raspuns = await fetch(`${BAZA}/${entry.doc}?t=${Date.now()}`, { cache: 'no-store' });
      if (!raspuns.ok) return '';

      const text = await raspuns.text();
      this.cacheDocs[entry.version] = text;
      return text;
    } catch (error) {
      console.warn(`Nu am putut citi documentația versiunii ${entry.version}:`, error);
      return '';
    }
  }
}

/**
 * Compară două versiuni semantice. Întoarce > 0 dacă `a` e mai nouă decât `b`.
 * Tolerant la valori lipsă sau malformate (le tratează ca 0).
 */
export const comparaVersiuni = (a: string, b: string): number => {
  const bucati = (v: string) => (v || '').split('.').map((x) => parseInt(x, 10) || 0);
  const [a1, a2, a3] = bucati(a);
  const [b1, b2, b3] = bucati(b);
  return a1 - b1 || a2 - b2 || a3 - b3;
};
